import { DefaultRoutes } from 'Routes';
import { useCallback } from 'react';
import { useReportId } from 'hooks/helperHooks';
import { useReportQueries } from 'utilities/reportQueriesFactory';
import useReportStore from './reportStore';
import { useStores } from 'index';
import { useReportLayoutManagement } from 'hooks/useReportLayoutManagement';
import { useShallow } from 'zustand/react/shallow';

// This hook connects queries and store with the rest of the app. This is the only hook that should be used to access data and queries.

const useBatterSessionReport = () => {
  const codegenReportService = useStores().codegenReportService;
  const { isCreate, reportId } = useReportId();

  const actions = useReportStore((state) => state.actions);

  const data = useReportStore(
    useShallow((state) => ({
      isEditMode: state.isEditMode,
      searchCriteria: state.searchCriteria,
      searchChips: state.searchChips,
      specialConfigOptions: state.specialConfigOptions,
      selectedConfgValues: state.selectedConfgValues,
      layout: state.layout,
      chartData: state.chartData,
      tempLayout: state.tempLayout,
      editingChart: state.editingChart,
    }))
  );

  const { isLoadingReport, saveOrgLayout, generateReportMutation, updateReportSetupMutation } = useReportQueries({
    reportRoute: DefaultRoutes.SessionReviewReport.BatterAggregated,
    useReportStore: useReportStore,
    getOrgLayoutFn: codegenReportService.getReportSessionReviewReportBatterAggregatedOrgLayout,
    saveOrgLayoutFn: codegenReportService.postReportSessionReviewReportBatterAggregatedOrgLayout,
    generateReportFn: codegenReportService.postReportSessionReviewReportBatterAggregatedGenerate,
    getReportDataFn: codegenReportService.postReportSessionReviewReportBatterAggregatedGetReportData,
    updateReportSetupFn: codegenReportService.postReportSessionReviewReportBatterAggregatedUpdateReportSetup,
    setReportMetadata: (reportData) => {
      const { pitchTypeInfo, chartData: pageData, ...rest } = reportData;
      return {
        newSearchChips: {
          seasonNames: rest.seasonNames,
          dateRanges: rest.dateRanges,
          levelName: rest.levelName,
          teamName: rest.teamName,
          playerNames: pageData.map((item) => item.playerName),
        },
        newSpecialConfigOptions: { pitchTypeInfo },
      };
    },
  });

  const { hasUnsavedEdits, discardLayoutChanges, saveLayoutForSession, saveLayoutForOrg } = useReportLayoutManagement({
    isEditMode: data.isEditMode,
    tempLayout: data.tempLayout,
    layout: data.layout,
    setTempLayout: actions.setTempLayout,
    setLayout: actions.setLayout,
    reportId,
    isCreate,
    selectedConfgValues: data.selectedConfgValues,
    updateReportSetupMutation,
    saveOrgLayout,
  });

  const getNewData = useCallback(() => {
    actions.setCanGetNewData(true);
  }, [actions]);

  const generateReport = useCallback(() => {
    actions.setCanGetNewData(false);
    const { gameType, dateRanges, seasonNames, level, team, players } = data.searchCriteria;

    if (data.layout && gameType && dateRanges && level && team && players) {
      generateReportMutation({
        requestBody: {
          gameType,
          dateRanges,
          seasonNames: seasonNames ?? [],
          level,
          team,
          players,
          reportContentConfig: data.selectedConfgValues,
          configData: data.layout,
        },
      });
    }
  }, [data.searchCriteria, data.layout, data.selectedConfgValues, actions, generateReportMutation]);

  const setSearchCriteria = useCallback(
    (criteria: Partial<typeof data.searchCriteria>) => {
      actions.setPartialSearchCriteria(criteria);
      actions.setPartialSearchChips({
        ...('dateRanges' in criteria && { dateRanges: criteria.dateRanges }),
        ...('seasonNames' in criteria && { seasonNames: criteria.seasonNames }),
        ...('level' in criteria && { levelName: criteria.level?.name }),
        ...('team' in criteria && { teamName: criteria.team?.name }),
        ...('players' in criteria && { playerNames: criteria.players?.map((p) => p.name) }),
      });
    },
    [actions]
  );

  return {
    ...data,
    ...actions,
    setSearchCriteria,
    isLoadingReport,
    generateReport,
    getNewData,
    hasUnsavedEdits,
    discardLayoutChanges,
    saveLayoutForSession,
    saveLayoutForOrg,
  };
};

export default useBatterSessionReport;
